import { getCurrencySymbol } from '@angular/common'
import {
  computed,
  DOCUMENT,
  effect,
  inject,
  Injectable,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthFacadeService } from '@wm-front/admin/auth/data-access'
import { map, shareReplay } from 'rxjs'

export interface CurrencySetting {
  currency: Currency
  countryCode: CountryCode
  isSymbolBefore: boolean
  IntLocale: Intl.Locale
}

export type Currency = 'EUR' | 'GBP' | 'SGD' | 'HKD'

export type CountryCode = 'GB' | 'FR' | 'SG' | 'HK' | 'PT'

export type Territory = 'en-GB' | 'fr-FR' | 'pt-PT' | 'en-SG' | 'en-HK'

@Injectable({
  providedIn: 'root',
})
export class L10nService {
  public _document = inject<Document>(DOCUMENT)

  public _auth = inject(AuthFacadeService)

  /** currently available territories for the current user */
  public readonly territories$ = toSignal(
    this._auth.adminInfo$.pipe(
      map((adminInfo) => adminInfo.territories),
      shareReplay(1)
    ),
    { initialValue: [] }
  )

  /** wether or not the L10N is inited & ready to be used */
  public readonly ready$ = computed(() => this.territories$().length > 0)

  /** currently selected territory */
  public readonly territory$ = signal<string | null>(null)

  /** the i18n name of the current territory */
  public readonly territoryName$ = computed(
    () =>
      this.territories$().find((t) => t.countryCode === this.territory$())?.name
  )

  /** the timezone of the current territory */
  public readonly timezone$ = computed(
    () => this._timezoneSettings[this.territory$() as CountryCode] || 'UTC'
  )

  /** the currency symbol of a territory */
  public readonly currencySymbol$ = computed(() => {
    const currency = this._currencySettings.find(
      (setting) => setting.countryCode === this.territory$()
    )?.currency
    if (!currency) return ''
    const symbol =
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value || ''
    return symbol
  })

  public readonly currency$ = computed(
    () =>
      this._currencySettings.find(
        (setting) => setting.countryCode === this.territory$()
      )?.currency || 'EUR'
  )

  /**
   * The i18n locale of the current territory.
   */
  public readonly territoryLocale$ = computed(
    () =>
      this._currencySettings.find(
        (setting) => setting.countryCode === this.territory$()
      )?.IntLocale || new Intl.Locale('en-GB')
  )

  /** Default territory of the app */
  private defaultTerritory!: string

  /** The window object */
  private window: Window

  /** The currency setting to calculate how the price should look like */
  private readonly _currencySettings: CurrencySetting[] = [
    {
      currency: 'GBP',
      isSymbolBefore: true,
      countryCode: 'GB',
      IntLocale: new Intl.Locale('en-GB'),
    },
    {
      currency: 'EUR',
      isSymbolBefore: false,
      countryCode: 'FR',
      IntLocale: new Intl.Locale('fr-FR'),
    },
    {
      currency: 'SGD',
      isSymbolBefore: true,
      countryCode: 'SG',
      IntLocale: new Intl.Locale('en-SG'),
    },
    {
      currency: 'EUR',
      isSymbolBefore: false,
      countryCode: 'PT',
      IntLocale: new Intl.Locale('pt-PT'),
    },
    {
      currency: 'HKD',
      isSymbolBefore: true,
      countryCode: 'HK',
      IntLocale: new Intl.Locale('en-HK'),
    },
  ]

  private readonly _timezoneSettings: { [key in CountryCode]: string } = {
    GB: 'Europe/London',
    FR: 'Europe/Paris',
    SG: 'Asia/Singapore',
    HK: 'Asia/Hong_Kong',
    PT: 'Europe/Lisbon',
  }

  constructor() {
    const _document = this._document

    this.window = _document.defaultView as Window

    effect(() => {
      /** Update it in storage */
      if (this.territory$()) {
        localStorage.setItem('wm-territory', this.territory$()!)
      }
    })

    effect(() => {
      if (this.territories$().length === 0) return
      this.ensureTerritoryIsInAllowedTerritories()
    })
  }

  /**
   * @deprecated use territory$ if you want the territory
   * @deprecated use territoryLocale$ if you want the locale of the territory
   * @deprecated use territoryName$ if you want the name of the territory
   */
  public get territoryLocale(): string {
    return this.territory$()!
  }

  /**
   * Initializes l10n for the application.
   * @param { string } defaultTerritory The default territory to use.
   * @param { string[] } supportedTerritories The list of supported territories.
   */
  public setAppDefault(defaultTerritory: string): void {
    this.defaultTerritory = defaultTerritory
    this.territory$.set(
      localStorage.getItem('wm-territory') || this.defaultTerritory
    )
  }

  /** Method to be called throughout the app
   * @param { string } territory the locale to be set
   */
  public setTerritory(wantedTerritory: string): void {
    if (this.territories$().find((t) => t.countryCode === wantedTerritory)) {
      this.territory$.set(wantedTerritory)
    } else {
      this.territory$.set(this.defaultTerritory)
    }

    /**
     * Reload the page
     * required until everyone is using the signals
     */
    this.window.location.reload()
  }

  /**
   * Will return the price properly formatted
   *
   * @todo this definitely breaks as soon as our territory name
   * are not locales. need to add something there
   * @param {string | number} value
   * @param {Currency} currency
   * @param {CountryCode} countryCode
   * @returns {string}
   */
  public getFormattedCurrency(
    value: string | number,
    currency?: Currency,
    countryCode?: CountryCode
  ): string {
    countryCode = countryCode || (this.territory$()! as CountryCode)
    const currencySetting = this.getCurrentCurrencySetting()
    if (!currency && countryCode) {
      const countrySetting = this._currencySettings.find(
        (setting) => setting.countryCode === countryCode
      )
      if (countrySetting) {
        currency = countrySetting.currency as Currency
      }
    }

    let result = ''
    if (typeof value === 'number' || !isNaN(Number(value))) {
      // @todo our territories might not be locales :)
      result = Intl.NumberFormat(
        currencySetting?.IntLocale || this.territory$()!,
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(Number(value))
    } else {
      result = value
    }
    const currencySymbol = getCurrencySymbol(currency as Currency, 'narrow')
    return `${
      currencySetting?.isSymbolBefore ? (currencySymbol || '-') + ' ' : ''
    }${result}${
      !currencySetting?.isSymbolBefore ? ' ' + (currencySymbol || '-') : ''
    }`
  }

  /**
   * Will return the currency setting
   * @returns {CurrencySetting | undefined}
   */
  public getCurrentCurrencySetting(): CurrencySetting | undefined {
    return this._currencySettings.find(
      (setting) => setting.countryCode === this.territory$()
    )
  }

  /**
   * Ensures that the current territory is in the list of allowed territories.
   * If not, it will set the territory to the default one.
   *
   * This should be called automatically when our territories change
   * @param territories
   */
  protected ensureTerritoryIsInAllowedTerritories() {
    const territories = this.territories$?.()
    // should never happen
    if (!territories?.length) return
    // our current territory is not in the list of available territories
    if (!territories.find((t) => t.countryCode === this.territory$())) {
      // If even our default territory is not in the list...
      if (!territories.find((t) => t.countryCode === this.defaultTerritory)) {
        // We update our default territory to the first one available
        this.defaultTerritory = territories[0]?.countryCode
      }

      // We set the territory to the default oned
      this.territory$.set(this.defaultTerritory)
    }
  }
}
